import React, { useCallback, useState } from "react";
import get from "lodash/get";
import { Plural, Trans, t } from "@lingui/macro";
import { withI18n } from "@lingui/react";
import { connect } from "react-redux";
import LazyHydrate from "react-lazy-hydration";
import loadable from "@loadable/component";
import { JsonLd } from "react-schemaorg";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import * as types from "../../stores/types";
import { isMobileUserAgent, getUserAgent } from "../../functions/userAgent";
import { Api } from "../../functions/fetchFromApi";
import toQueryString from "../../functions/toQueryString";
import { getViewed } from "../../functions/viewedTours";
import { getProductLink } from "../../functions/getProductLink";
import { fetchOverview } from "../../functions/fetchData";
import removeNullKeys from "../../functions/removeNullKeys";
import reverseUrl from "../../functions/reverseUrl";
import { sendListViewedEvent } from "../../functions/analytics";
import { fetchDefaultCurrency } from "../../functions/currency";
import getCurrentLanguage from "../../functions/languages/getCurrentLanguage";
import withRedirectToKnownLang from "../../functions/languages/withRedirectToKnownLang";
import ScrollHook from "../../components/ScrollHook";
import TopBar from "../../components/TopBar";
import Root from "../../components/_Root";
import ErrorBoundary from "../../components/ErrorBoundary";
import { CanonicalAuto } from "../../components/Canonical";
import { AlternateAuto } from "../../components/Alternate";
import { DEFAULT_LANG } from "../../constants";
import { usePageViewTracking } from "../../functions/usePageViewTracking";
import withArrows from "../../components/ProductsSlider/withArrows";
import "./Main.css";
import AudioGuideSection from "../../components/AudioGuideSection/Index";
import { BestTours } from "../../components/BestTours";
import banner from "../ErrorPage/assets/banner.svg";
import inclineIfNeeded from "../../functions/inclineIfNeeded";

// Компоненты первого экрана с высоким приоритетом
const ProductCard = loadable(() => import("../../components/ProductCard"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: true,
});

// Компоненты второго экрана
const ProductSlider = loadable(() => import("../../components/ProductsSlider"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: false,
});

const ProductTabs = loadable(() => import("../../components/ProductsTabs"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: false,
});

const ProductsGrid = loadable(() => import("../../components/ProductsGrid"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: false,
});

// Компоненты нижней части страницы
const Banner = loadable(() => import("../../components/Banner"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: false,
});

// Компоненты третьего экрана
const BestCities = loadable(() => import("../../components/BestCities"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: false,
});

const BestPlaces = loadable(() => import("../../components/BestPlaces"), {
  ssr: true,
  fallback: <div style={{ height: "200px" }} />,
  priority: false,
});

/**
 * Modifying components
 */
const ProductCarousel = withArrows(ProductSlider);

function Viewed({ lang = DEFAULT_LANG, products = [] }) {
  const { isDesktop } = useMediaQueries();
  const slidesPerPage = isDesktop ? 4 : 3;
  const productListId = "viewed";

  /**
   * Product list became visible in viewport
   */
  const onProductListShown = useCallback(() => {
    sendListViewedEvent(productListId, products, lang);
  }, [productListId, products, lang]);

  return (
    <div className="Main__recently">
      <h2 className="Main__recently_title">
        <Trans>Recently viewed</Trans>
      </h2>
      <ScrollHook once="shown" showOn=".Main__recently" onChanged={onProductListShown} />
      <div className="Wrapper__overload">
        <ProductCarousel flatOnMobile chainedScroll slidesPerPage={slidesPerPage}>
          {products.map((product, i) =>
            product ? (
              <div key={product.id}>
                <ProductCard
                  product={product}
                  link={getProductLink(lang, product)}
                  position={i}
                  listId={productListId}
                />
              </div>
            ) : null,
          )}
        </ProductCarousel>
      </div>
    </div>
  );
}

function Popular({ lang, products = [] }) {
  const productListId = "popular_worldwide";
  /**
   * Product list became visible in viewport
   */
  const onProductListShown = () => {
    sendListViewedEvent(productListId, products, lang);
  };

  return (
    <div className="Main__popular">
      <h2 className="Main__popular_title">
        <Trans>TOP activities worldwide</Trans>
      </h2>
      <ScrollHook once="shown" showOn=".Main__popular" onChanged={onProductListShown} />
      <ProductsGrid withFavorite products={products} listId={productListId} />
    </div>
  );
}

const Destinations = withI18n()(function Destinations({ lang, cities = [], countries = [], i18n }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const tabNames = [i18n._(t`TOP destinations`), i18n._(t`TOP countries to visit`)];

  const handleTabChange = index => setSelectedTab(index);

  const TabContent = ({ data, type }) => {
    if (!Array.isArray(data) || data.length === 0) {
      return (
        <div className="Main__linking__empty">
          <img src={banner} alt="Banner" className="Linking__banner-image" />
          <p className="Linking__empty-text">Nothing here yet</p>
        </div>
      );
    }

    return (
      <div className="Main__destinations_tabs">
        {data.map(item => {
          const to = reverseUrl(type, { lang, [`${type}Slug`]: item.slug, [`${type}Id`]: item.id });
          return (
            <a key={item.id} href={to} className="destination-item">
              <div className="destination-item__name">{item.name}</div>
              <div className="destination-item__count">
                <Plural
                  value={item.itemsCount || 5}
                  _0="# tours & tickets"
                  one="# tour & ticket"
                  few="# tours & tickets"
                  many="# tours & tickets"
                  other="# tours & tickets"
                />
              </div>
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <div className="Main__destinations">
      <ProductTabs tabNames={tabNames} selectedTab={selectedTab} onChanged={handleTabChange}>
        {selectedTab === 0 && <TabContent data={cities} type="city" />}
        {selectedTab === 1 && <TabContent data={countries} type="country" />}
      </ProductTabs>
    </div>
  );
});

function Main({
  lang,
  overview = {},
  products = {},
  viewed = [],
  destinations = [],
  countryCities,
  worldCities,
  worldCountries,
  countryName,
}) {
  const [headerSearch, setHeaderSearch] = useState(false);
  usePageViewTracking("main");
  const TopCities = overview?.topCities;
  const countryNameNormalized = countryName ? inclineIfNeeded(countryName, "in", lang) : "";
  return (
    <Root
      stickyHeader
      hideAppMarkets
      isMainPage
      searchInHeader={headerSearch}
      destinations={destinations}
      title={t`WeGoTrip — Audio Tours & Tourist Audio Guides around the world`}
      description={t`WeGoTrip — Find high-quality audio guides with tickets to attractions from experts for self-travel around the world.`}
    >
      <AlternateAuto route="main" />
      <CanonicalAuto route="main" />
      <ScrollHook showOn=".TopBar__search .Search__row" edge="bottom" onChanged={setHeaderSearch} />
      <TopBar />
      <JsonLd
        item={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "WeGoTrip",
          url: "https://wegotrip.com",
          logo: "https://wegotrip.com/logo.png",
          sameAs: [
            "https://www.facebook.com/wegotrip",
            "https://twitter.com/wegotrip",
            "https://www.instagram.com/wegotrip",
          ],
        }}
      />
      <div className="Wrapper Main">
        <LazyHydrate whenVisible>
          <AudioGuideSection />
        </LazyHydrate>
        <ErrorBoundary>
          <LazyHydrate whenVisible>
            {viewed.length ? <Viewed lang={lang} products={viewed} /> : null}
          </LazyHydrate>
        </ErrorBoundary>

        {countryCities?.length > 0 && (
          <ErrorBoundary>
            <LazyHydrate whenVisible>
              <div>
                <h2 className="Main__best-tours_title">
                  <Trans>Best tours in {countryNameNormalized}</Trans>
                </h2>
                <BestTours cities={countryCities} />
              </div>
            </LazyHydrate>
          </ErrorBoundary>
        )}

        <ErrorBoundary>
          <LazyHydrate whenVisible>
            {products.products?.length ? (
              <Popular products={products.products} lang={lang} />
            ) : null}
          </LazyHydrate>
        </ErrorBoundary>

        <ErrorBoundary>
          <LazyHydrate whenVisible>
            <BestCities topCities={TopCities} lang={lang} />
          </LazyHydrate>
        </ErrorBoundary>

        <ErrorBoundary>
          <LazyHydrate whenVisible>
            <div>
              <h2 className="Main__best-places_title">
                <Trans>Best places worldwide</Trans>
              </h2>
              <BestPlaces cities={worldCities} />
            </div>
          </LazyHydrate>
        </ErrorBoundary>
      </div>
      <LazyHydrate whenVisible>
        <div className="Wrapper Main__bottom">
          <ErrorBoundary>
            <Destinations lang={lang} cities={worldCities} countries={worldCountries} />
          </ErrorBoundary>

          <LazyHydrate whenVisible>
            <Banner />
          </LazyHydrate>
        </div>
      </LazyHydrate>
    </Root>
  );
}

Main.getInitialProps = withRedirectToKnownLang(async ({ req, match, store }) => {
  const userAgent = getUserAgent(req);
  const mobile = isMobileUserAgent(userAgent);
  const lang = getCurrentLanguage(match.params.lang);

  const cacheKey = `main_page_${lang}_${mobile ? "mobile" : "desktop"}`;

  const cachedData = await store.getState().cache?.get(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  const [currency, overview] = await Promise.all([
    fetchDefaultCurrency(req),
    fetchOverview(
      {
        lang,
        expand_overview: "fee,entrance",
        limit: mobile ? 6 : 12,
      },
      req,
    ),
  ]);

  store.dispatch({
    type: types.FETCH_USER,
    cookies: get(req, "headers.cookie"),
  });

  store.dispatch({ type: types.SET_DEFAULT_CURRENCY, defaultCurrency: currency });

  const { currentCountry: { id: countryId, name: countryName } = {} } = removeNullKeys(overview);

  const countryCities = (
    await Api.get(`/api/v2/cities/?country=${countryId}&lang=${lang}&preorder=true`, {
      lang,
    })
  ).data.results;

  const perPage = mobile ? 24 : 48;

  const worldCities = (
    await Api.get(`/api/v2/cities/?lang=${lang}&preorder=true&page=1&per_page=${perPage}`, {
      lang,
    })
  ).data.results;

  const worldCountries = (
    await Api.get(`/api/v2/countries/?lang=${lang}&page=1&per_page=${perPage}`, {
      lang,
    })
  ).data.results;

  const productsParams = {
    lang,
    perPage: mobile ? 6 : 8,
    expand: "images",
    preorder: true,
    sort: "popular",
  };

  await store.dispatch({
    type: types.FETCH_PRODUCTS,
    ...productsParams,
  });

  const destinations = overview.allDestinations || [];
  const categories = destinations.filter(city => city.category);

  const productIds = getViewed(get(req, "headers.cookie"));
  let viewedTours = [];

  if (productIds.length) {
    try {
      const viewedResponse = await Api.get(
        `/api/v2/products/?${toQueryString({
          lang,
          currency,
          product_id: productIds.slice(0, 4).join(","),
          expand: "images",
          preorder: true,
        })}`,
        { lang },
      );

      viewedTours = viewedResponse.data.results;
    } catch (error) {
      console.error("Error on loading info about viewed tours", error);
    }
  }

  const result = {
    overview,
    lang,
    countryName,
    countryCities,
    worldCities,
    worldCountries,
    mobile,
    viewed: viewedTours.filter(product => product.locale === lang),
    destinations,
    categories,
  };

  await store.getState().cache?.set(cacheKey, result, 300);

  return result;
});

const mapStateToProps = ({ products }) => ({
  products,
});

export default connect(mapStateToProps)(Main);
